// Versioned localStorage adapter for Pinia stores.
//
// Each store owns ONE localStorage key (e.g. `nodecast.filters`). The schema
// version is stored alongside the data; on read, if the version is older
// than what the store knows about, we run the registered migrations in
// order. This replaces the legacy SPA's 15-key sprawl with one key per
// store + a deliberate migration path.

import { log } from '@/lib/log';

export interface PersistedShape<T> {
    schemaVersion: number;
    data: T;
}

export type Migration<T> = (prev: unknown) => T;

export interface PersistOptions<T> {
    /** localStorage key, e.g. `nodecast.filters`. */
    key: string;
    /** Current schema version of T. Bump this whenever T changes shape. */
    version: number;
    /** Map an older schema's payload into the current shape. Indexed by FROM-version. */
    migrations?: Record<number, Migration<T>>;
    /** Called when the persisted blob is missing or unmigrateable. */
    fallback: () => T;
    /** Optional legacy keys to drain in (key, parser) pairs — used for the cutover from public/ keys. */
    legacyKeys?: Array<{ key: string; map: (raw: string) => Partial<T> | null }>;
}

export function loadPersisted<T>(opts: PersistOptions<T>): T {
    let raw: string | null = null;
    try {
        raw = localStorage.getItem(opts.key);
    } catch {
        return opts.fallback();
    }

    let value: T = opts.fallback();
    let needsWrite = false;

    if (raw) {
        try {
            const parsed = JSON.parse(raw) as PersistedShape<unknown>;
            if (typeof parsed === 'object' && parsed !== null && 'schemaVersion' in parsed) {
                if (parsed.schemaVersion === opts.version) {
                    value = parsed.data as T;
                } else if (parsed.schemaVersion < opts.version && opts.migrations) {
                    let migrated: unknown = parsed.data;
                    for (let v = parsed.schemaVersion; v < opts.version; v++) {
                        const m = opts.migrations[v];
                        if (!m) {
                            log.warn('persist.migration_missing', { key: opts.key, from: v });
                            migrated = null;
                            break;
                        }
                        migrated = m(migrated);
                    }
                    if (migrated !== null) {
                        value = migrated as T;
                        needsWrite = true;
                        log.info('persist.migrated', {
                            key: opts.key,
                            from: parsed.schemaVersion,
                            to: opts.version,
                        });
                    }
                }
            }
        } catch (err) {
            log.warn('persist.parse_failed', { key: opts.key, error: (err as Error).message });
        }
    }

    if (!raw && opts.legacyKeys && opts.legacyKeys.length) {
        // Cutover from old SPA keys: read each, merge into shape, then drop them.
        let drained = false;
        for (const { key, map } of opts.legacyKeys) {
            try {
                const legacyRaw = localStorage.getItem(key);
                if (legacyRaw === null) continue;
                const partial = map(legacyRaw);
                if (partial) {
                    value = { ...value, ...partial };
                    drained = true;
                }
                localStorage.removeItem(key);
            } catch {
                // ignore one bad key
            }
        }
        if (drained) {
            needsWrite = true;
            log.info('persist.legacy_drained', {
                key: opts.key,
                count: opts.legacyKeys.length,
            });
        }
    }

    if (needsWrite) savePersisted(opts.key, opts.version, value);
    return value;
}

export function savePersisted<T>(key: string, version: number, data: T): void {
    try {
        const blob: PersistedShape<T> = { schemaVersion: version, data };
        localStorage.setItem(key, JSON.stringify(blob));
    } catch (err) {
        log.warn('persist.write_failed', { key, error: (err as Error).message });
    }
}
